<template>
  <v-data-table
    v-if="modelConnectionParameterList"
    :headers="headers"
    :items="filteredItems"
    :loading="loading"
    density="comfortable"
    v-model:items-per-page="itemsPerPage"
    show-select
    select-strategy="single"
    v-model="selection"
    return-object
    fixed-header
    :height="props.height"
    :hide-default-footer="props.hideFooter"
  >
    <template v-slot:item.sectionId="{ item }">
      {{ getSectionName(item) }}
    </template>
    <template v-slot:item.terminalsystemId="{ item }">
      {{ getTerminalSystemName(item) }}
    </template>

    <template v-slot:item.actions="{ item }">
      <v-btn density="compact" icon variant="plain" v-if="!item.isConfigured" :ripple="false">
        <v-icon icon="mdi-alert-octagon" color="error"></v-icon>
        <v-tooltip open-delay="300" activator="parent" location="top">{{ t('model.noConfigured') }}</v-tooltip>
      </v-btn>
      <v-btn density="compact" icon variant="plain" v-if="item.hasNoConnection" :ripple="false">
        <v-icon icon="mdi-connection" color="warning" size="small"></v-icon>
        <v-tooltip open-delay="300" activator="parent" location="top">{{ t('model.hasNoConnection') }}</v-tooltip>
      </v-btn>
    </template>
    <template v-slot:no-data>
      <v-btn color="#6b4da9" @click="debouncedLoadItems()">{{ t('button.reload') }} </v-btn>
    </template>
    <template #bottom v-if="!props.hideFooter">
      <div class="d-flex align-center flex-column flex-sm-row pa-3">
        <div class="d-flex align-center">
          <span class="text-caption me-2 mt-2">{{ $vuetify.locale.t('$vuetify.dataFooter.itemsPerPageText') }}</span>
          <v-select class="ultra-compact-select" v-model="itemsPerPage" :items="[15, 25, 50, 100]" variant="plain" density="compact" hide-details></v-select>
        </div>
        <v-spacer></v-spacer>
        <v-pagination v-model="page" :length="pageCount" :total-visible="7" show-first-last-page variant="text" density="comfortable" size="small"></v-pagination>
        <v-spacer></v-spacer>
        <div class="flex-1-1-0"></div>
      </div>
    </template>
  </v-data-table>
</template>

<script setup>
import { ref, computed, defineEmits } from 'vue';
import { useI18n } from 'vue-i18n';
import debounce from 'lodash.debounce';

const { t } = useI18n();
const emit = defineEmits(['reload-items']);
const props = defineProps({
  height: {
    type: String,
    default: 'calc(100vh - 290px)',
  },
  hideFooter: {
    type: Boolean,
    default: false,
  },
});

const modelConnectionParameterList = defineModel('connectionParameters', { type: Array, default: () => [] });
const sections = defineModel('sections', { type: Array, default: () => [] });
const terminalSystems = defineModel('terminalSystems', { type: Array, default: () => [] });
const loading = defineModel('loading', { type: Boolean, default: true });
const selection = defineModel('selection', { type: Array, default: () => [] });
const search = defineModel('search', { type: String, default: '' });
const page = defineModel('page', { type: Number, default: 1 });

const itemsPerPage = ref(15);
const headers = ref([
  {
    title: t('model.section'),
    key: 'sectionId',
    sortable: false,
  },
  {
    title: t('model.description'),
    key: 'description',
    sortable: false,
  },
  { title: 'URL', key: 'url' },
  {
    title: 'Terminalsystem',
    key: 'terminalsystemId',
    sortable: false,
  },
  { title: t('model.requiredActions'), key: 'actions', align: /** @type {const} */ ('center'), sortable: false },
]);

const filteredItems = computed(() => {
  const q = search.value.trim().toLowerCase();
  if (!q) return modelConnectionParameterList.value;
  return modelConnectionParameterList.value.filter((item) => Object.values(item).some((v) => String(v).toLowerCase().includes(q)));
});

const pageCount = computed(() => {
  return Math.ceil(filteredItems.value.length / itemsPerPage.value) || 1;
});

const getSectionName = (verbindungsparameter) => {
  const sectionName = sections.value.find((x) => x.id === verbindungsparameter.sectionId)?.description;
  return sectionName;
};

const getTerminalSystemName = (verbindungsparameter) => {
  if (verbindungsparameter.terminalsystemId) {
    const terminalSystemName = terminalSystems.value.find((x) => x.id === verbindungsparameter.terminalsystemId)?.description;
    return terminalSystemName;
  }

  return '';
};

const debouncedLoadItems = debounce(() => {
  emit('reload-items', search.value);
}, 300);
</script>
